import React, { useEffect } from "react";
import type { Issue } from "../model/validation";
import type { Conflict } from "../import/analyze";

export function FullIdPreview({ namespace, id }: { namespace: string; id: string }) {
  return (
    <code className="fullid-preview" title="Composed at export time, never stored in the project">
      {namespace || "?"}:{id || "?"}
    </code>
  );
}

export function IssueList({ issues }: { issues: Issue[] }) {
  if (issues.length === 0) return <p className="ok">No problems found.</p>;
  return (
    <ul className="issue-list">
      {issues.map((issue, i) => (
        <li key={i} className={`issue ${issue.severity}`}>
          <span className="badge">{issue.severity}</span> {issue.message}
        </li>
      ))}
    </ul>
  );
}

export function ConflictList({ conflicts }: { conflicts: Conflict[] }) {
  if (conflicts.length === 0) return <p className="ok">No conflicts detected.</p>;
  return (
    <ul className="issue-list">
      {conflicts.map((c, i) => (
        <li key={i} className={`issue ${c.severity}`}>
          <span className="badge">{c.severity}</span> {c.message}
          {c.where && c.where.length > 0 && (
            <div className="where">
              {c.where.map((w, j) => (
                <code key={j}>{w}</code>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export function Modal({ title, onClose, children, actions }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {actions && <footer className="modal-actions">{actions}</footer>}
      </div>
    </div>
  );
}

interface LocalizedFieldProps {
  label: string;
  locale: string;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  multiline?: boolean;
  placeholder?: string;
}

/** Editor for a per-language string map, scoped to the active locale. */
export function LocalizedField({ label, locale, value, onChange, multiline, placeholder }: LocalizedFieldProps) {
  const current = value[locale] ?? "";
  const setValue = (v: string) => onChange({ ...value, [locale]: v });
  return (
    <label className="field">
      <span className="field-label">
        {label} <span className="locale-tag">{locale}</span>
      </span>
      {multiline ? (
        <textarea rows={4} value={current} placeholder={placeholder} onChange={(e) => setValue(e.target.value)} />
      ) : (
        <input value={current} placeholder={placeholder} onChange={(e) => setValue(e.target.value)} />
      )}
    </label>
  );
}

export function EmptyState({ children }: { children: React.ReactNode }) {
  return <div className="empty-state">{children}</div>;
}
